import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { PsHttpService, type PatSnapSuccessResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';
import type { InitAiSpecificationTaskApi, InitAiSpecificationTaskRequest, InitAiSpecificationTaskResponse } from '../init-ai-specification-task';
import type { DetectLanguageApi, DetectLanguageRequest, DetectLanguageResponse } from '../detect-language';
import type { GetInputContentApi, GetInputContentRequest, GetInputContentResponse } from '../get-input-content';
import type { SubmitConfirmDisclosureContentApi, SubmitConfirmDisclosureContentRequest, SubmitConfirmDisclosureContentResponse } from '../submit-confirm-disclosure-content';
import type { GetClassificationApi, GetClassificationRequest, GetClassificationResponse } from '../get-classification';
import { FactoryPsHttpSSE, PsHttpSSE } from '@/services/sse-ps-http/sse-ps-http';
import { getCurrentLanguage } from '@telecom/shared/utils/language-tool';
import type { GenTechnicalApi, GenTechnicalRequest, GenTechnicalResponse } from '../gen-technical';
import type { GenTechnologyCategoryApi, GenTechnologyCategoryRequest, GenTechnologyCategoryResponse } from '../gen-technology-category';
import type { SubmitConfirmClaimsContentApi, SubmitConfirmClaimsContentRequest, SubmitConfirmClaimsContentResponse } from '../submit-confirm-claims-content';
import type { GenFeatureTreeApi, GenFeatureTreeRequest, GenFeatureTreeResponse } from '../gen-feature-tree';
import type { FormatClaimApi, FormatClaimRequest, FormatClaimResponse } from '../format-claim';
import type { SpecificationGenParagraphApi, SpecificationGenParagraphRequest, SpecificationGenParagraphResponse } from '../gen-specification-paragraph';
import type { SpecificationAiOptimizeParagraphApi, SpecificationAiOptimizeParagraphRequest, SpecificationAiOptimizeParagraphResponse } from '../ai-optimize-specification-paragraph';
import type { SpecificationStreamContent } from '../../types/specification';
import type { GenEmbodimentApi, GenEmbodimentRequest, GenEmbodimentResponse, GenEmbodimentFromOutlineRequest, SaveEmbodimentFromOutlineRequest, SaveEmbodimentFromOutlineResponse, DeleteEmbodimentWithOutlineRequest, DeleteEmbodimentWithOutlineResponse } from '../gen-embodiment-api';
import type { BatchUploadFiguresApi, BatchUploadFiguresRequest, BatchUploadFiguresResponse } from '../batch-upload-figures-api';
import type { GetFiguresApi, GetFiguresRequest, GetFiguresResponse } from '../get-figures';
import type { GetTermsApi, GetTermsRequest, GetTermsResponse } from '../get-terms';
import type { CreateEmbodimentOutlineApi, CreateEmbodimentOutlineRequest, CreateEmbodimentOutlineResponse, SubmitEmbodimentOutlineRequest, SubmitEmbodimentOutlineResponse, ModifyEmbodimentOutlineRequest, ModifyEmbodimentOutlineResponse } from '../create-embodiment-outline-api';
import type { FileParseApi, FileParseRequest, DocumentParseResponse } from '../file-parse-api';
import type { FileUploadApi, FileUploadRequest, FileUploadResponse } from '../file-upload-api';
import type { FileDeleteApi, FileDeleteRequest } from '../file-delete-api';
import type { ParsedFiguresApi, SaveParsedFiguresRequest, SaveParsedFiguresResponse, GetParseImagesRequest, GetParseImagesRequestResponse } from '../parsed-figures-api';
import type { WireFrameExtractApi, SaveWireFrameChoiceApi, WireFrameExtractRequest, SaveWireFrameChoiceRequest, SaveWireFrameChoiceResponse, WireFrameExtractResponse } from '../wire-frame-extract-api';
import type { DeleteFigureApi, DeleteFigureRequest, DeleteFigureResponse } from '../delete-figure-api';
import _ from 'lodash';
import type { ReorderFiguresApi, ReorderFiguresRequest, ReorderFiguresResponse } from '../reorder-figures-api';


@injectable()
export class SpecificationApiImpl extends PsHttpService
    implements InitAiSpecificationTaskApi,
    DetectLanguageApi,
    GetInputContentApi,
    SubmitConfirmDisclosureContentApi,
    GenTechnologyCategoryApi,
    GenTechnicalApi,
    GetClassificationApi,
    SubmitConfirmClaimsContentApi,
    GenFeatureTreeApi,
    FormatClaimApi,
    SpecificationAiOptimizeParagraphApi,
    SpecificationGenParagraphApi,
    BatchUploadFiguresApi,
    FileParseApi,
    FileUploadApi,
    FileDeleteApi,
    ParsedFiguresApi,
    GenEmbodimentApi,
    GetFiguresApi,
    GetTermsApi,
    WireFrameExtractApi,
    SaveWireFrameChoiceApi,
    CreateEmbodimentOutlineApi,
    ReorderFiguresApi,
    DeleteFigureApi {

    async reorderFigures(task_id: string, data: ReorderFiguresRequest): Promise<ReorderFiguresResponse> {
        const result = await this.put<PatSnapSuccessResponseData<ReorderFiguresResponse>>({
            url: `/ai-specification/task/${task_id}/figures/reorder`,
            data,
        });
        return result.data;
    }


    get options() {
        return {
            baseURL: coreTelecomApiUrlPrefix,
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }

    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE;

    async initAiSpecificationTask(data: InitAiSpecificationTaskRequest): Promise<InitAiSpecificationTaskResponse> {
        const result = await this.post<PatSnapSuccessResponseData<InitAiSpecificationTaskResponse>>({
            url: '/ai-specification/task/init',
            data,
        });
        return result.data;
    }

    async batchUploadFigures(request: BatchUploadFiguresRequest): Promise<BatchUploadFiguresResponse> {
        const result = await this.post<PatSnapSuccessResponseData<BatchUploadFiguresResponse>>({
            url: `/ai-specification/task/${request.taskId}/image-upload`,
            data: request.form,
        });
        return result.data;
    }

    async parseFile(request: FileParseRequest): Promise<DocumentParseResponse> {
        const formData = new FormData();
        formData.append('file', request.file);

        const result = await this.post<PatSnapSuccessResponseData<DocumentParseResponse>>({
            url: `/ai-specification/task/${request.taskId}/file-parse?content_type=${request.contentType}`,
            data: formData,
        });
        return result.data;
    }

    async uploadFile(request: FileUploadRequest): Promise<FileUploadResponse> {
        const formData = new FormData();
        formData.append('file', request.file);

        const result = await this.post<PatSnapSuccessResponseData<boolean>>({
            url: `/ai-specification/task/${request.taskId}/file-upload?content_type=${request.contentType}`,
            data: formData,
        });
        return { success: result.data };
    }

    async saveParsedFigures(request: SaveParsedFiguresRequest): Promise<SaveParsedFiguresResponse> {
        const result = await this.post<PatSnapSuccessResponseData<SaveParsedFiguresResponse>>({
            url: `/ai-specification/task/${request.taskId}/parsed-figures`,
            data: { image_s3_keys: request.image_s3_keys },
        });
        return result.data;
    }

    async getParseImages(request: GetParseImagesRequest): Promise<GetParseImagesRequestResponse> {
        const result = await this.get<PatSnapSuccessResponseData<GetParseImagesRequestResponse>>({
            url: `/ai-specification/task/${request.taskId}/parse-images`,
        });
        return result.data;
    }

    async detectLanguage(data: DetectLanguageRequest): Promise<DetectLanguageResponse> {
        const result = await this.post<PatSnapSuccessResponseData<DetectLanguageResponse>>({
            url: '/ai-specification/lang-detect',
            data,
        });
        return result.data;
    }

    async getInputContent(data: GetInputContentRequest): Promise<GetInputContentResponse> {
        try {
            const result = await this.post<PatSnapSuccessResponseData<GetInputContentResponse>>({
                url: '/ai-specification/input-content',
                data,
            });
            return result.data;
        } catch (error) {
            return {
                jurisdiction: 'CNIPA',
                disclosure: '',
                claim: '',
            };
        }
    }

    async getClassification(data: GetClassificationRequest, options?: { single: AbortSignal }): Promise<GetClassificationResponse> {
        const result = await this.post<PatSnapSuccessResponseData<GetClassificationResponse>>({
            url: '/ai-specification/classification',
            data,
            signal: options?.single,
        });
        return result.data;
    }

    async submitConfirmDisclosureContent(data: SubmitConfirmDisclosureContentRequest): Promise<SubmitConfirmDisclosureContentResponse> {
        const result = await this.put<PatSnapSuccessResponseData<SubmitConfirmDisclosureContentResponse>>({
            url: '/ai-specification/content/modify',
            data: {
                ...data,
                current_step: 'SPECIFICATION_DISCLOSURE_RESULT',
            },
        });
        return result.data;
    }

    private _specificationSseContent(data: any): PsHttpSSE<SpecificationStreamContent<any>> {
        const sse = this._factorySsePsHttp<any>().init({
            baseURL: coreTelecomApiUrlPrefix,
            url: '/ai-specification/content',
            data,
            headers: {
                lang: getCurrentLanguage(),
                from: 's-core-analytics',
            },
        });
        return sse;
    }

    genTechnologyCategory(data: GenTechnologyCategoryRequest): PsHttpSSE<SpecificationStreamContent<GenTechnologyCategoryResponse>> {
        return this._specificationSseContent({
            ...data,
            current_step: 'SPECIFICATION_DISCLOSURE_RESULT',
            content_type: 'category',
        });
    }

    genTechnical(data: GenTechnicalRequest): PsHttpSSE<SpecificationStreamContent<GenTechnicalResponse>> {
        return this._specificationSseContent({
            ...data,
            current_step: 'SPECIFICATION_DISCLOSURE_RESULT',
            content_type: 'tech_wrapper',
        });
    }

    genEmbodiment(data: GenEmbodimentRequest): PsHttpSSE<SpecificationStreamContent<GenEmbodimentResponse>> {
        return this._specificationSseContent({
            ...data,
            current_step: 'SPECIFICATION_DISCLOSURE_RESULT',
            content_type: 'embodiment_extract',
        });
    }

    async submitConfirmClaimsContent(data: SubmitConfirmClaimsContentRequest): Promise<SubmitConfirmClaimsContentResponse> {
        const result = await this.put<PatSnapSuccessResponseData<SubmitConfirmClaimsContentResponse>>({
            url: '/ai-specification/content/modify',
            data: {
                ...data,
                current_step: 'CLAIM_PARSER',
            },
        });
        return result.data;
    }

    genFeatureTree(data: GenFeatureTreeRequest): PsHttpSSE<SpecificationStreamContent<GenFeatureTreeResponse>> {
        return this._specificationSseContent({
            ...data,
            current_step: 'CLAIM_PARSER',
            content_type: 'feature_tree',
        });
    }

    async formatClaim(data: FormatClaimRequest, options?: { single: AbortSignal }): Promise<FormatClaimResponse> {
        const result = await this.post<PatSnapSuccessResponseData<FormatClaimResponse>>({
            url: '/ai-specification/claim/format',
            data,
            signal: options?.single,
        });
        return result.data;
    }

    genSpecificationParagraph(data: SpecificationGenParagraphRequest): PsHttpSSE<SpecificationStreamContent<SpecificationGenParagraphResponse>> {
        return this._specificationSseContent({
            ...data,
            current_step: 'SPECIFICATION_RESULT',
            content_type: data.content_type,
        });
    }

    aiOptimizeSpecificationParagraph(data: SpecificationAiOptimizeParagraphRequest): PsHttpSSE<SpecificationStreamContent<SpecificationAiOptimizeParagraphResponse>> {
        const sse = this._factorySsePsHttp<any>().init({
            url: `${ coreTelecomApiUrlPrefix }/ai-specification/content/${data.optimize_type}`,
            data,
            headers: {
                lang: getCurrentLanguage(),
                from: 's-core-analytics',
            },
        });
        return sse;
    }

    async getFigures(data: GetFiguresRequest): Promise<GetFiguresResponse> {
        const result = await this.get<PatSnapSuccessResponseData<GetFiguresResponse>>({
            url: `/ai-specification/task/${data.task_id}/content/figures`,
        });
        return result.data;
    }

    async getTerms(data: GetTermsRequest): Promise<GetTermsResponse> {
        const result = await this.get<PatSnapSuccessResponseData<GetTermsResponse>>({
            url: `/ai-specification/task/${data.task_id}/content/terms`,
        });
        return result.data;
    }

    async createEmbodimentOutline(data: CreateEmbodimentOutlineRequest): Promise<CreateEmbodimentOutlineResponse> {
        const result = await this.post<PatSnapSuccessResponseData<CreateEmbodimentOutlineResponse>>({
            url: `${coreTelecomApiUrlPrefix}/ai-specification/embodiment/outline`,
            data,
        });
        return result.data;
    }

    async submitEmbodimentOutline(taskId: string, data: SubmitEmbodimentOutlineRequest): Promise<SubmitEmbodimentOutlineResponse> {
        const result = await this.put<PatSnapSuccessResponseData<SubmitEmbodimentOutlineResponse>>({
            url: `${coreTelecomApiUrlPrefix}/ai/task/${taskId}/content/embodiment_outline`,
            data,
        });
        return result.data;
    }

    genEmbodimentFromOutline(data: GenEmbodimentFromOutlineRequest): PsHttpSSE<SpecificationStreamContent<string>> {
        const sse = this._factorySsePsHttp<any>().init({
            baseURL: coreTelecomApiUrlPrefix,
            url: '/ai-specification/embodiment/content',
            data,
            headers: {
                lang: getCurrentLanguage(),
                from: 's-core-analytics',
            },
        });
        return sse;
    }

    async saveEmbodimentFromOutline(data: SaveEmbodimentFromOutlineRequest): Promise<SaveEmbodimentFromOutlineResponse> {
        const result = await this.put<PatSnapSuccessResponseData<SaveEmbodimentFromOutlineResponse>>({
            url: '/ai-specification/embodiment/content',
            data,
        });
        return result.data;
    }

    async getEmbodimentOutline(taskId: string): Promise<CreateEmbodimentOutlineResponse> {
        const result = await this.get<PatSnapSuccessResponseData<CreateEmbodimentOutlineResponse>>({
            url: `${coreTelecomApiUrlPrefix}/ai/task/content/${taskId}/embodiment_outline`,
        });
        return result.data;
    }

    async modifyEmbodimentOutline(data: ModifyEmbodimentOutlineRequest): Promise<ModifyEmbodimentOutlineResponse> {
        const result = await this.post<PatSnapSuccessResponseData<ModifyEmbodimentOutlineResponse>>({
            url: '/ai-specification/embodiment/outline/modify',
            data,
        });
        return result.data;
    }

    async deleteEmbodimentWithOutline(data: DeleteEmbodimentWithOutlineRequest): Promise<DeleteEmbodimentWithOutlineResponse> {
        const result = await this.delete<PatSnapSuccessResponseData<DeleteEmbodimentWithOutlineResponse>>({
            url: '/ai-specification/embodiment/content',
            data,
        });
        return result.data;
    }

    async deleteFile(data: FileDeleteRequest): Promise<boolean> {
        const result = await this.delete<PatSnapSuccessResponseData<boolean>>({
            url: `/ai-specification/task/${data.taskId}/file`,
            params: {
                content_type: data.contentType,
            },
        });
        return result.data;
    }

    async extractWireFrame(request: WireFrameExtractRequest, signal?: AbortSignal): Promise<WireFrameExtractResponse> {
        const result = await this.post<PatSnapSuccessResponseData<WireFrameExtractResponse>>({
            url: `/ai-specification/task/${request.taskId}/wireframe-extract`,
            data: _.omit(request, 'taskId'),
            signal,
        });
        return result.data;
    }

    async saveWireFrameChoice(request: SaveWireFrameChoiceRequest): Promise<SaveWireFrameChoiceResponse> {
        const result = await this.post<PatSnapSuccessResponseData<SaveWireFrameChoiceResponse>>({
            url: `/ai-specification/task/${request.taskId}/wireframe-result/save`,
            data: _.omit(request, 'taskId'),
        });
        return result.data;
    }

    async deleteFigure(request: DeleteFigureRequest): Promise<DeleteFigureResponse> {
        const result = await this.delete<PatSnapSuccessResponseData<DeleteFigureResponse>>({
            url: `/ai-specification/task/${request.taskId}/figures/${request.figureId}`,
        });
        return result.data;
    }
}
